import type { GetApiV1CompetitionIdGroupsParams, GetApiV1CompetitionScriptParams } from '@1webit/cvs-api-scheme/models';
import { queryOptions } from '@tanstack/react-query';

import { DEFAULT_TABLE_PAGE_SIZE, FUTURE_SEASON } from '@/constants';
import { getResponse, isValueEmpty, queryParams } from '@/utils';

export type AdditionalSeason = 'FUTURE_SEASON' | 'CURRENT_SEASON';

export const getApiV1CompetitionSeasons = (additionalSeason?: AdditionalSeason | AdditionalSeason[]) =>
  queryOptions({
    queryKey: ['getApiV1CompetitionSeasons', additionalSeason],
    queryFn: async () => {
      const res = await getResponse('getApiV1CompetitionSeasons');
      const options = res.map(({ season }) => ({
        value: season,
        label: season,
      }));

      const additionalSeasons = Array.isArray(additionalSeason) ? additionalSeason : [additionalSeason];

      if (additionalSeasons.includes('CURRENT_SEASON')) {
        options.unshift({
          value: '',
          label: 'aktuální',
        });
      }
      if (additionalSeasons.includes('FUTURE_SEASON')) {
        options.unshift({
          value: FUTURE_SEASON,
          label: 'budoucí',
        });
      }

      return options;
    },
  });

/**
 * Gets folders seasons
 * @param folderId
 */
export const getApiV1CompetitionFolderFolderIdSeasons = (folderId: number) => queryOptions({
  queryKey: ['getApiV1CompetitionFolderFolderIdSeasons', folderId],
  queryFn: async () => {
    const res = await getResponse('getApiV1CompetitionFolderFolderIdSeasons', folderId);
    return res.map(({ season }) => ({
      value: season,
      label: season,
    }));
  },
});

/**
 * Gets folders competitions
 * @param folderId
 * @param season
 */
export const getApiV1CompetitionFolderFolderIdCompetitions = (folderId: number, season?: string) => queryOptions({
  queryKey: ['getApiV1CompetitionFolderFolderIdCompetitions', folderId, season],
  queryFn: () => getResponse('getApiV1CompetitionFolderFolderIdCompetitions', folderId, isValueEmpty(season) ? undefined : { season }),
});

/**
 * Gets competition categories
 */
export const getApiV1CategoryList = queryOptions({
  queryKey: ['getApiV1CategoryList'],
  queryFn: () => getResponse('getApiV1CategoryList'),
});

// competition id
export const getApiV1CompetitionId = (id: number) => queryOptions({
  queryKey: ['getApiV1CompetitionId', id],
  queryFn: () => getResponse('getApiV1CompetitionId', id),
});
export const getApiV1CompetitionIdGroups = (id: number, params?: GetApiV1CompetitionIdGroupsParams) => queryOptions({
  queryKey: ['getApiV1CompetitionIdGroups', id, params],
  queryFn: () => getResponse('getApiV1CompetitionIdGroups', id, params),
});

export const getApiV1GroupGroupIdTable = (groupId: number, teamsIds?: number[]) =>
  queryOptions({
    queryKey: ['getApiV1GroupGroupIdTable', groupId, teamsIds],
    queryFn: () =>
      getResponse(
        'getApiV1GroupGroupIdTable',
        groupId,
        queryParams({
          filtered_team_ids: teamsIds !== undefined && teamsIds.length > 0 ? teamsIds.join(',') : undefined,
        }),
      ),
  });

export const getApiV1CompetitionWhisper = (query: string, season?: string) => {
  return queryOptions({
    queryKey: ['getApiV1CompetitionWhisper', query, season],
    queryFn: () => getResponse('getApiV1CompetitionWhisper', queryParams({ search: query, season })),
  });
};

export const getApiV1CompetitionRegionWhisper = (query: string) => queryOptions({
  queryKey: ['getApiV1CompetitionRegionWhisper', query],
  queryFn: () => getResponse('getApiV1CompetitionRegionWhisper', { search: query }),
});

export const getApiV1CompetitionFolderFolderIdSubfolders = (folderId: number) => queryOptions({
  queryKey: ['getApiV1CompetitionFolderFolderIdSubfolders', folderId],
  queryFn: () => getResponse('getApiV1CompetitionFolderFolderIdSubfolders', folderId),
});

export const getApiV1CompetitionGroupGroupIdReferee = (groupId: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1CompetitionGroupGroupIdReferee', groupId, page, limit, sort],
    queryFn: () => getResponse('getApiV1CompetitionGroupGroupIdReferee', groupId, {
      limit,
      offset: (page - 1) * limit,
      sort,
    }),
  });
};

export const getApiV1CompetitionFavorite = queryOptions({
  queryKey: ['getApiV1CompetitionFavorite'],
  queryFn: () => getResponse('getApiV1CompetitionFavorite'),
});

export const getApiV1GroupFavorite = queryOptions({
  queryKey: ['getApiV1GroupFavorite'],
  queryFn: () => getResponse('getApiV1GroupFavorite'),
});

export const getApiV1CompetitionScript = (params?: GetApiV1CompetitionScriptParams) =>
  queryOptions({
    queryKey: ['getApiV1CompetitionScript', params],
    queryFn: () => getResponse('getApiV1CompetitionScript', params),
  });

export const getApiV1CompetitionSeasonsPlain = () =>
  queryOptions({
    queryKey: ['getApiV1CompetitionSeasons'],
    queryFn: () => getResponse('getApiV1CompetitionSeasons'),
  });
// script
export const getApiV1CompetitionScripts = (groupId: number) => queryOptions({
  queryKey: ['getApiV1CompetitionScripts', groupId],
  queryFn: async () => {
    return (await getResponse('getApiV1CompetitionScripts', { groupId })).map(script => ({
      value: script.id,
      label: script.name,
    }));
  },
});

// systems
export const getApiV1CompetitionSystems = queryOptions({
  queryKey: ['getApiV1CompetitionSystems'],
  queryFn: () => getResponse('getApiV1CompetitionSystems'),
});

// user rights

export const getApiV1CompetitionUserRights = (competitionId?: number, groupId?: number) => {
  if (competitionId === undefined && groupId === undefined) {
    throw new Error('Both competitionId and groupId are undefined');
  }

  return queryOptions({
    queryKey: ['getApiV1CompetitionUserRights', competitionId, groupId],
    queryFn: () => getResponse('getApiV1CompetitionUserRights', {
      ...(competitionId !== undefined ? { competitionId } : {}),
      ...(groupId !== undefined ? { groupId } : {}),
    }),
  });
};

export const getApiV1GroupGroupIdBracket = (groupId: number) => queryOptions({
  queryKey: ['getApiV1GroupGroupIdBracket', groupId],
  queryFn: () => getResponse('getApiV1GroupGroupIdBracket', groupId),
});

export const getApiV1GroupGroupIdPhotoReportMatchNumber = (groupId: number, matchNumber: number) => queryOptions<Response>({
  queryKey: ['getApiV1GroupGroupIdPhotoReportMatchNumber', groupId, matchNumber],
  queryFn: () => getResponse('getApiV1GroupGroupIdPhotoReportMatchNumber', groupId, matchNumber),
});

export const getApiV1GroupGroupIdPossibleOrganizers = (groupId: number) =>
  queryOptions({
    queryKey: ['getApiV1GroupGroupIdPossibleOrganizers', groupId],
    queryFn: () =>
      getResponse('getApiV1GroupGroupIdPossibleOrganizers', groupId),
  });

export const getApiV1GroupGroupIdTableFilter = (groupId: number) =>
  queryOptions({
    queryKey: ['getApiV1GroupGroupIdTableFilter', groupId],
    queryFn: () => getResponse('getApiV1GroupGroupIdTableFilter', groupId),
  });

export const getApiV1CompetitionIdBalls = (competitionId: number) => queryOptions({
  queryKey: ['getApiV1CompetitionIdBalls', competitionId],
  queryFn: () => getResponse('getApiV1CompetitionIdBalls', competitionId),
});
